import moment from "moment";

import { MetricPeriodType, PortfolioNamesDocument } from "~/graphql/generated/graphql";
import type { ResultsPerPageOption } from "~/helpers/pagination-helpers";

import { type BenchmarkGroupOption, type OpexRowItem, type OpexSettingsTemplate, type PropertyOpexColumn } from "~/helpers/modules/opex-helpers";
import type { BalanceDateRange } from "~/helpers/modules/accounts-helpers";
import { TenanciesUserPreferenceColumns, type TenanciesColumnKey, type TenanciesDate } from "~/helpers/entities/tenancy-helpers";
import { FrontpageWidget } from "~/helpers/frontpage-helpers";
import { ApolloClient } from "@apollo/client/core";
import { cloneDeep } from "~/helpers/common-helpers";

/** Note Sven: Deprecated, but still used as a type for migration */
export type NamespacePortfolioPropertyTenanciesColumnSettings = {
  [k in TenanciesColumnKey]: {
    enabled: boolean;
    position: number;
  };
};

// export type NamespacePortfolioPropertyTenancies = {
//   selectedDate: TenanciesDate;
//   columns: TenanciesColumnKey[];
//   pageSize: number;
// };

const getDefaultTenancyColumnSettings = () => TenanciesUserPreferenceColumns.filter((header) => header.enabled).map((header) => header.key);

export enum NumberDisplay {
  Absolute = "ABSOLUTE",
  Thousands = "THOUSANDS",
  Millions = "MILLIONS",
}

export type NamespaceChrome = {
  splitPercentage: number;
  containerFocusIndex: number;
};

export interface DateRangeConfig {
  periodType: MetricPeriodType;
  dateRange: BalanceDateRange;
  customDateStart: string | undefined;
  customDateEnd: string | undefined;
  referenceDate?: string;
}

export type PeriodRangeType = "yearWhole" | "yearRolling" | "yearToDate";

export type BudgetsConfig = Record<string, { id: string; name: string; description?: string | null; validFrom?: string | null }[]>;

interface PreferencesNamespaceBase {
  type: PreferencesNamespaceType;
}

export interface NamespaceLocale extends PreferencesNamespaceBase {
  type: "preferenceNamespaceLocale";
  locale: string;
}

export interface NamespacePortfolioPortfolio extends PreferencesNamespaceBase {
  type: "preferenceNamespacePortfolioPortfolio";

  churnFilterDateTo: string;
  churnFilterDateFrom: string;
  churnFilterPeriodType: MetricPeriodType;

  idleTenancyPercentFilterDateTo: string;
  idleTenancyPercentFilterDateFrom: string;
  idleTenancyPercentFilterPeriodType: MetricPeriodType;

  rentFilterDateTo: string;
  rentFilterDateFrom: string;
  rentFilterPeriodType: MetricPeriodType;
}

export interface NamespacePortfolioPropertyOverview extends PreferencesNamespaceBase {
  type: "preferenceNamespacePortfolioPropertyOverview";
  idleTenancyRateFilterDateTo: string;
  idleTenancyRateFilterDateFrom: string;
  idleTenancyRateFilterPeriodType: MetricPeriodType;

  evictionRateFilterDateTo: string;
  evictionRateFilterDateFrom: string;
  evictionRateFilterPeriodType: MetricPeriodType;

  rentRateFilterDateTo: string;
  rentRateFilterDateFrom: string;
  rentRateFilterPeriodType: MetricPeriodType;
}

export interface NamespaceOpex extends PreferencesNamespaceBase {
  type: "preferencesNamespaceOpex";
  template: string;
  templateModified: boolean;
  savedTemplates: OpexSettingsTemplate[];
  includeZeroRows: boolean;
  benchmark: BenchmarkGroupOption;
  periodRangeType: PeriodRangeType;
  periodOffset: number;
  normaliseBy: "tenancies" | "area" | "none";
  excludePropertyIds: ("properties" | "companies" | string)[];
  referenceMonthOffset: number | undefined;
  referencePercentage: boolean;
  includePortfolioIds?: string[];
  includeCategories?: DataItem[];
}

export interface NamespacePropertyOpex extends PreferencesNamespaceBase {
  type: "preferencesNamespacePropertyOpex";
  includeZeroRows: boolean;
  periodRangeType: PeriodRangeType;
  periodOffset: number;
  includeCategories?: OpexRowItem[];
  showColumns: PropertyOpexColumn[];
  showPerAreaColumns: PropertyOpexColumn[];
  propertyBudgets: Record<string, string[]>;
}

export interface NamespacePortfolioPropertyBalance extends PreferencesNamespaceBase, DateRangeConfig {
  type: "preferencesNamespacePortfolioPropertyBalance";
  portfolioId: Nullable<string>;
  propertyId: Nullable<string>;
  numberDisplay: NumberDisplay;
  includeZeroAccounts: boolean;
  includeZeroCategories: boolean;
  useChangeColors: boolean;
  showBudgetsTotal: boolean;
  showBudgetForecasts: boolean;
  showBudgetDiff: boolean;
  propertyBudgets: BudgetsConfig;
  showColumns: {
    totalCostWithVat: boolean;
    change: boolean;
    changePercentage: boolean;
  };
}

export interface NamespacePortfolioPropertyTenancies extends PreferencesNamespaceBase {
  type: "preferencesNamespacePortfolioPropertyTenancies";

  selectedDate: TenanciesDate;
  columns: TenanciesColumnKey[];
  pageSize: number;
}

export interface PreferencesNamespaceFrontpage extends PreferencesNamespaceBase {
  type: "preferencesNamespaceFrontpage";

  widgets: Widget[];
}

type PreferencesNamespace =
  | NamespaceLocale
  | NamespacePortfolioPortfolio
  | NamespacePortfolioPropertyOverview
  | NamespacePortfolioPropertyTenancies
  | NamespacePortfolioPropertyBalance
  | NamespacePropertyOpex
  | NamespaceOpex
  | PreferencesNamespaceFrontpage;

type PreferencesNamespaceType = PreferencesNamespace["type"];

export type UserPreferences = {
  preferenceNamespaceLocale: NamespaceLocale;

  preferencesNamespacePropertyOpex: NamespacePropertyOpex;

  preferencesNamespaceOpex: NamespaceOpex;

  preferencesNamespacePortfolioOpex: NamespaceOpex;

  preferencesNamespacePortfolioPropertyBalance: NamespacePortfolioPropertyBalance;

  preferenceNamespacePortfolioPortfolio: NamespacePortfolioPortfolio;

  preferenceNamespacePortfolioPropertyOverview: NamespacePortfolioPropertyOverview;

  preferenceNamespacePortfolioPropertyTenancies: NamespacePortfolioPropertyTenancies;

  frontpage: PreferencesNamespaceFrontpage;
};

export type NamespaceName = keyof UserPreferences;
export type Namespace = PreferencesNamespace;

export const DEFAULTS: {
  [k in NamespaceName]: UserPreferences[k] | ((preferences: UserPreferences[k] | undefined, client: ApolloClient<any>) => Promise<UserPreferences[k]>);
} = {
  preferenceNamespaceLocale: {
    type: "preferenceNamespaceLocale",

    locale: "da-dk",
  },

  preferencesNamespacePropertyOpex: {
    type: "preferencesNamespacePropertyOpex",

    propertyBudgets: {},
    includeZeroRows: false,
    periodOffset: 0,
    periodRangeType: "yearToDate",
    showColumns: [],
    showPerAreaColumns: [],
    includeCategories: undefined,
  },

  preferencesNamespaceOpex: {
    type: "preferencesNamespaceOpex",

    savedTemplates: [],
    template: "normLatest12",
    templateModified: false,
    includeZeroRows: false,
    benchmark: "average.total",
    periodOffset: 0,
    periodRangeType: "yearWhole",
    normaliseBy: "area",
    excludePropertyIds: [],
    includePortfolioIds: undefined,
    includeCategories: undefined,
    referenceMonthOffset: undefined,
    referencePercentage: false,
  },

  preferencesNamespacePortfolioOpex: {
    type: "preferencesNamespaceOpex",

    savedTemplates: [],
    template: "normLatest12",
    templateModified: false,
    includeZeroRows: false,
    benchmark: "average.total",
    periodOffset: 0,
    periodRangeType: "yearWhole",
    normaliseBy: "area",
    excludePropertyIds: [],
    includeCategories: undefined,
    referenceMonthOffset: undefined,
    referencePercentage: false,
  },

  preferencesNamespacePortfolioPropertyBalance: {
    type: "preferencesNamespacePortfolioPropertyBalance",

    propertyId: undefined,
    portfolioId: undefined,
    dateRange: "latest8",
    customDateEnd: undefined,
    customDateStart: undefined,
    periodType: MetricPeriodType.Quarterly,
    numberDisplay: NumberDisplay.Absolute,
    includeZeroAccounts: false,
    includeZeroCategories: true,
    useChangeColors: false,
    showBudgetsTotal: false,
    showBudgetForecasts: false,
    showBudgetDiff: false,
    propertyBudgets: {},
    showColumns: {
      totalCostWithVat: true,
      change: false,
      changePercentage: false,
    },
  },

  preferenceNamespacePortfolioPortfolio: {
    type: "preferenceNamespacePortfolioPortfolio",

    churnFilterDateTo: moment().utc().format("YYYY-MM-DD"),
    churnFilterDateFrom: moment().subtract(5, "year").utc().format("YYYY-MM-DD"),
    churnFilterPeriodType: MetricPeriodType.Quarterly,

    idleTenancyPercentFilterDateTo: moment().utc().format("YYYY-MM-DD"),
    idleTenancyPercentFilterDateFrom: moment().subtract(5, "year").utc().format("YYYY-MM-DD"),
    idleTenancyPercentFilterPeriodType: MetricPeriodType.Quarterly,

    rentFilterDateTo: moment().utc().format("YYYY-MM-DD"),
    rentFilterDateFrom: moment().subtract(5, "year").utc().format("YYYY-MM-DD"),
    rentFilterPeriodType: MetricPeriodType.Quarterly,
  },

  preferenceNamespacePortfolioPropertyOverview: {
    type: "preferenceNamespacePortfolioPropertyOverview",

    idleTenancyRateFilterDateTo: moment().utc().format("YYYY-MM-DD"),
    idleTenancyRateFilterDateFrom: moment().subtract(5, "year").utc().format("YYYY-MM-DD"),
    idleTenancyRateFilterPeriodType: MetricPeriodType.Quarterly,

    evictionRateFilterDateTo: moment().utc().format("YYYY-MM-DD"),
    evictionRateFilterDateFrom: moment().subtract(5, "year").utc().format("YYYY-MM-DD"),
    evictionRateFilterPeriodType: MetricPeriodType.Quarterly,

    rentRateFilterDateTo: moment().utc().format("YYYY-MM-DD"),
    rentRateFilterDateFrom: moment().subtract(5, "year").utc().format("YYYY-MM-DD"),
    rentRateFilterPeriodType: MetricPeriodType.Quarterly,
  },

  preferenceNamespacePortfolioPropertyTenancies: async (preferences) => {
    const columnsFromOldSettings = migrateOldTenanciesColumnSettings(preferences);

    return {
      type: "preferencesNamespacePortfolioPropertyTenancies",

      pageSize: preferences?.pageSize ?? 25,
      selectedDate: preferences?.selectedDate ?? "today",
      columns: preferences?.columns ?? columnsFromOldSettings ?? getDefaultTenancyColumnSettings(),
    };
  },

  frontpage: async (preferences, client) => {
    const getWidgets = async (): Promise<Widget[]> => {
      const portfolios = await client.query({
        query: PortfolioNamesDocument,
        variables: {
          filters: { groups: [{ matches: [{ active: true }] }] },
        },
      });

      return [
        widgetCountDefault("portfolio"),
        widgetCountDefault("property"),
        widgetCountDefault("tenancy"),
        ...[...portfolios.data.assetManagementPortfolios.items]
          .sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 0))
          .map((pf) => widgetPortfolioDefault(pf.id)),
      ];
    };

    return {
      type: "preferencesNamespaceFrontpage",
      widgets: preferences?.widgets ?? (await getWidgets()),
    };
  },
};

const migrateOldTenanciesColumnSettings = (preferences?: Object) => {
  if (!preferences || "includeColumns" in preferences === false) return;

  const oldColumns = preferences as { includeColumns: { global: NamespacePortfolioPropertyTenanciesColumnSettings } };

  return Object.entries(oldColumns.includeColumns.global)
    .filter(([, value]) => value.enabled)
    .sort(([, a], [, b]) => a.position - b.position)
    .map(([key]) => key as TenanciesColumnKey);
};

const setNamespaceDefaults = <T extends Namespace>(defaultPreferencesNamespaced: T, savedPreferencesNamespaced: T): T => {
  const output = {} as typeof defaultPreferencesNamespaced;

  let key: keyof T;

  for (key in defaultPreferencesNamespaced)
    output[key] = savedPreferencesNamespaced[key] === undefined ? defaultPreferencesNamespaced[key] : savedPreferencesNamespaced[key];

  return output;
};

export const getNamespace = async <T extends NamespaceName>(
  namespace: T,
  preferences: Partial<UserPreferences>,
  client: ApolloClient<any>
): Promise<UserPreferences[T]> => {
  const defaultPreferences = DEFAULTS[namespace];

  const resolvedDefaultPreferences = (
    typeof defaultPreferences === "function" ? await defaultPreferences(preferences[namespace], client) : cloneDeep(defaultPreferences)
  ) as UserPreferences[T];

  if (preferences[namespace] === undefined) {
    return resolvedDefaultPreferences;
  } else {
    return setNamespaceDefaults<UserPreferences[T]>(resolvedDefaultPreferences, preferences[namespace]);
  }
};
